import { useEffect, useId, useRef, useState } from 'react'

import { FxButton, FxIcon, FxTextField } from '@/components/fx'
import {
  BINARY_LABELS,
  binaryState,
  binaryValue,
  clearsValue,
  reviewSubmission,
  submitLabel,
  type BinaryState,
  type ReviewFieldType,
} from '@/components/review'
import { cn } from '@/lib/utils'
import type { TextType } from '@/types/domain'

/**
 * One row's value control: the input (or the segments) and the button that
 * commits it.
 *
 * **Why this is not `FieldValueEditor`.** The shared editor is a column — a
 * labelled field, a line of prose saying what the submit will record, and two
 * full-width buttons. That is right for the per-document screen, where one
 * field fills a panel and the reviewer is deciding about *this* field. Here
 * thirty of them are read down a column, and the whole value of the screen is
 * that the eye travels a short distance: crop, value, Enter. A 160px-tall
 * editor per row would make the list scroll four times as far and turn a
 * rhythm into a chore.
 *
 * What it does **not** change is the rules: the submission is still
 * `reviewSubmission(value, draft)`, so unchanged is still an acceptance, an
 * emptied field is still the correction to `''` that records "hier steht
 * nichts", and the binary segments still never post on selection — Enter or
 * the button commits. The row says which of the three it will do in the
 * button's own label ("Als leer speichern" is deliberately not "Übernehmen"),
 * since that is the only place left for it.
 */

export interface RowValueEditorProps {
  label: string
  type: ReviewFieldType
  /** The delivered value — `resultFieldValue(field)` via the queue item. */
  value: string
  textType?: TextType
  /** Changes when the editor must forget its draft. The queue item's key: a
   *  row keeps its half-typed correction across the re-render an optimistic
   *  write causes, and only a different field resets it. */
  resetKey: string
  onSubmit: (value: string) => void
  busy?: boolean
  /** The row the keyboard is on. Takes focus when it becomes true. */
  active?: boolean
  onFocus?: () => void
  className?: string
}

const INPUT_MODE: Partial<Record<TextType, React.HTMLAttributes<HTMLInputElement>['inputMode']>> = {
  number: 'numeric',
  phone: 'tel',
  email: 'email',
  postcode: 'numeric',
  url: 'url',
}

export function RowValueEditor({
  label,
  type,
  value,
  textType,
  resetKey,
  onSubmit,
  busy,
  active,
  onFocus,
  className,
}: RowValueEditorProps) {
  const [draft, setDraft] = useState(value)
  const input = useRef<HTMLInputElement>(null)
  const first = useRef<HTMLInputElement>(null)
  const groupId = useId()
  const noticeId = `${groupId}-notice`

  useEffect(() => {
    setDraft(value)
    // Only a change of field resets the draft. Watching `value` would take a
    // correction away mid-typing, because the optimistic write changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  useEffect(() => {
    if (!active) return
    const node = type === 'text' ? input.current : first.current
    if (!node || node === document.activeElement) return
    node.focus()
    if (type === 'text') node.select()
  }, [active, resetKey, type])

  const submission = reviewSubmission(value, draft)
  // Emptying a field that held something is its own act, and in a list of
  // thirty rows there is no room for a sentence explaining each one — so the
  // button is renamed and a single line names what is about to be discarded.
  const clearing = clearsValue(submission)

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (busy) return
    onSubmit(draft)
  }

  return (
    <form
      onSubmit={submit}
      onFocus={onFocus}
      className={cn('flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:gap-4', className)}
    >
      {type === 'text' ? (
        <div className="flex min-w-0 grow flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <FxTextField
              ref={input}
              aria-label={`Wert für ${label}`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              inputMode={textType ? INPUT_MODE[textType] : undefined}
              placeholder="Wert eingeben"
              // NOT FxTextField's own note: it renders a bare <input> when it
              // has neither a label nor a note and a wrapped one when a note
              // appears, so a note arriving the instant somebody empties the
              // field remounts the input — and remounting takes the caret out
              // of it mid-correction. The line is rendered below instead,
              // wired to the input by hand.
              aria-describedby={clearing ? noticeId : undefined}
              disabled={busy}
              className={cn(
                'min-w-0 grow',
                clearing && 'shadow-[shadow:var(--fx-shadow-raised),inset_0_0_0_2px_hsl(var(--fx-danger))]',
              )}
            />
            {/* Emptying the box by hand does the same thing; the button is how
                a reviewer finds out it is allowed. It clears the draft only —
                the write still goes through the submit. */}
            <FxButton
              type="button"
              variant="outlined"
              icon="backspace"
              aria-label={`Feld „${label}“ als leer markieren`}
              onClick={() => {
                setDraft('')
                input.current?.focus()
              }}
              disabled={busy || draft === ''}
              className="shrink-0"
            >
              Leer
            </FxButton>
          </div>
          {clearing && (
            <p id={noticeId} className="text-caption text-danger-text">
              Wird als leer gespeichert — „{value}“ wird verworfen.
            </p>
          )}
        </div>
      ) : (
        <fieldset className="flex min-w-0 grow flex-col gap-1" disabled={busy}>
          <legend className="sr-only">{`Wert für ${label}`}</legend>
          <div className="flex flex-wrap gap-2">
            {/* "Undefiniert" is offered everywhere now: a correction with an
                empty value is how a reviewer records that nothing is marked
                here, and the endpoint stores it. */}
            {(['yes', 'no', 'unknown'] as BinaryState[]).map((state, index) => {
              const id = `${groupId}-${state}`
              const selected = binaryState(draft) === state
              return (
                <div key={state}>
                  <input
                    ref={index === 0 ? first : undefined}
                    type="radio"
                    id={id}
                    name={groupId}
                    className="peer sr-only"
                    checked={selected}
                    // Selection changes the draft only. In a real radio
                    // group the arrow keys move *and* select, so posting on
                    // change would write a wrong value on every arrow press.
                    onChange={() => setDraft(binaryValue(state))}
                  />
                  <label
                    htmlFor={id}
                    className={cn(
                      // `font-semibold`, not `text-body-strong`: tailwind-merge
                      // cannot tell the ramp class from a text colour and drops one.
                      'flex h-control cursor-pointer items-center gap-2 rounded-md px-4 text-small font-semibold',
                      'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-strong',
                      selected
                        ? 'bg-primary-strong text-on-primary'
                        : 'bg-fill text-text hover:bg-primary-subtle hover:text-primary-text',
                    )}
                  >
                    {state !== 'unknown' && (
                      <FxIcon name={state === 'yes' ? 'check_box' : 'check_box_outline_blank'} size={18} />
                    )}
                    {BINARY_LABELS[type][state]}
                  </label>
                </div>
              )
            })}
          </div>
          {clearing && (
            <p className="text-caption text-danger-text">
              Wird als leer gespeichert — {BINARY_LABELS[type][binaryState(value)]} wird verworfen.
            </p>
          )}
        </fieldset>
      )}

      <FxButton
        type="submit"
        variant={clearing ? 'danger' : submission.kind === 'correct' ? 'primary' : 'successTonal'}
        disabled={busy}
        className="shrink-0"
      >
        {submitLabel(submission)}
      </FxButton>
    </form>
  )
}
